import formReducer, {
  FormReducerAction,
  emptySingleInputField,
  emptyListField,
  formHelperMethodFactory,
  emptyMultiInputField,
} from 'reducers/FormReducer/FormReducer'
import {
  FormReducerSingleTextField,
  FormReducerListField,
  PickByType,
  FormReducerMultiTextField,
  ValidationObject,
  withValidationFactory,
  createFormField,
} from 'reducers/FormReducer/utils'
import type { BrandsDetailData } from '@contracts'
import brandLanguageFormReducer, {
  BrandLanguageFormReducerState,
  emptyBrandLanguageFormReducerState,
} from 'reducers/CreateAndEditRetailerReducer/RetailerBrandLanguageReducer'
import { addNewBlockItem, removeBlockItem, BlockTypes } from 'reducers/CreateAndEditRetailerReducer/utils'

export interface BrandFormReducerState {
  id: FormReducerSingleTextField
  brand_name: FormReducerSingleTextField
  target_audience: FormReducerSingleTextField
  tone_of_voice: FormReducerSingleTextField
  guidelines: FormReducerListField
  guidelines_shortener: FormReducerListField
  restrictions: FormReducerListField
  brand_language: BrandLanguageFormReducerState[]
  keyword_priority: FormReducerMultiTextField
  keyword_required: FormReducerMultiTextField
  keyword_optional: FormReducerMultiTextField
  keyword_omit: FormReducerMultiTextField
  keyphrase_required: FormReducerMultiTextField
  keyphrase_optional: FormReducerMultiTextField
}

type PickBrandStateField<T> = PickByType<BrandFormReducerState, T>

// Define the keys of the fields that match the correct element types
export type UpdateBrandSingleInputField = PickBrandStateField<FormReducerSingleTextField>
export type UpdateBrandMultiInputField = PickBrandStateField<FormReducerMultiTextField>
export type UpdateBrandListField = PickBrandStateField<FormReducerListField>

export const defaultBrandErrorObject: ValidationObject<'BrandFormReducerState'> = {}
const withValidation = withValidationFactory(defaultBrandErrorObject)

export const initialBrandFormReducerState = (data: BrandsDetailData): BrandFormReducerState => ({
  id: createFormField('text', ''),
  brand_name: withValidation(createFormField('text', data.brand_name || ''), 'brand_name', ['requiredField'], []),
  target_audience: createFormField('text', data.config?.content_config?.style_guide?.target_audience || ''),
  tone_of_voice: createFormField('text', data.config?.content_config?.style_guide?.tone_of_voice || ''),
  guidelines: createFormField('list', data.config?.content_config?.guidelines || []),
  guidelines_shortener: createFormField('list', data.config?.content_config?.guidelines_shortener || []),
  restrictions: createFormField('list', data.config?.content_config?.restrictions || []),
  brand_language: (data.brand_language?.map(lang => ({
    region: createFormField('text', lang.region || ''),
    patterns: createFormField('multiText', lang.patterns || []),
    replacement: createFormField('text', lang.replacement || ''),
  })) || []) as unknown as BrandLanguageFormReducerState[],
  keyword_priority: createFormField('multiText', data.brand_keywords?.priority || []),
  keyword_required: createFormField('multiText', data.brand_keywords?.required || []),
  keyword_optional: createFormField('multiText', data.brand_keywords?.optional || []),
  keyword_omit: createFormField('multiText', data.brand_keywords?.omit || []),
  keyphrase_required: createFormField('multiText', data.brand_keywords?.keyphrases_required || []),
  keyphrase_optional: createFormField('multiText', data.brand_keywords?.keyphrases_optional || []),
})

export const emptyBrandFormReducerState: BrandFormReducerState = {
  id: emptySingleInputField,
  brand_name: withValidation(emptySingleInputField, 'brand_name', ['requiredField'], []),
  target_audience: emptySingleInputField,
  tone_of_voice: emptySingleInputField,
  guidelines: emptyListField,
  guidelines_shortener: emptyListField,
  restrictions: emptyListField,
  brand_language: [],
  keyword_priority: emptyMultiInputField,
  keyword_required: emptyMultiInputField,
  keyword_optional: emptyMultiInputField,
  keyword_omit: emptyMultiInputField,
  keyphrase_required: emptyMultiInputField,
  keyphrase_optional: emptyMultiInputField,
}

// action types for reducer
const UPDATE_CONTENT_BLOCK = 'UPDATE_CONTENT_BLOCK'
const TRANSFORM_CONTENT_BLOCKS = 'TRANSFORM_CONTENT_BLOCKS'
const UPDATE_BRAND_LANGUAGE = 'UPDATE_BRAND_LANGUAGE'
const TRANSFORM_BRAND_LANGUAGE = 'TRANSFORM_BRAND_LANGUAGE'

// extend the form reducer to handle brand specific fields
export type BrandFormReducerAction =
  | FormReducerAction
  | {
      type: typeof UPDATE_CONTENT_BLOCK
      payload: {
        action: FormReducerAction
        idx: number
      }
    }
  | {
      type: typeof TRANSFORM_CONTENT_BLOCKS
      payload: {
        actionType: 'add' | 'remove' | 'shift'
        idx: number | null
      }
    }
  | {
      type: typeof UPDATE_BRAND_LANGUAGE
      payload: {
        action: FormReducerAction
        idx: number
      }
    }
  | {
      type: typeof TRANSFORM_BRAND_LANGUAGE
      payload: {
        actionType: 'add' | 'remove'
        idx: number | null
      }
    }

const brandFormReducer = (state: BrandFormReducerState, action: BrandFormReducerAction): BrandFormReducerState => {
  const { type, payload } = action

  switch (type) {
    case 'UPDATE_SINGLE_INPUT':
    case 'UPDATE_MULTI_INPUT':
    case 'UPDATE_LIST':
    case 'UPDATE_SELECT':
    case 'UPDATE_MULTI_SELECT':
    case 'SET_SELECT':
    case 'HANDLE_ERROR':
    case 'HANDLE_WARNING':
    case 'RESET_FORM':
      return formReducer<'BrandFormReducerState'>(state, action)

    // Delegate the brand language state update to the brand language reducer
    case UPDATE_BRAND_LANGUAGE: {
      const brandLanguage = [...state.brand_language]
      brandLanguage[action.payload.idx] = brandLanguageFormReducer(
        brandLanguage[action.payload.idx],
        action.payload.action,
      )
      return {
        ...state,
        brand_language: brandLanguage,
      }
    }
    // handle the transformation of the brand language
    case TRANSFORM_BRAND_LANGUAGE: {
      const { actionType, idx } = payload
      let brandLanguage = [...state.brand_language]
      switch (actionType) {
        case 'add': {
          if (idx !== null) throw new Error('idx should be null for add action')
          brandLanguage = addNewBlockItem<'BrandLanguageFormReducerState'>(
            brandLanguage,
            emptyBrandLanguageFormReducerState,
          )

          break
        }
        case 'remove': {
          if (idx === null) throw new Error('idx should not be null for remove action')
          brandLanguage = removeBlockItem<'BrandLanguageFormReducerState'>(brandLanguage, idx)
          break
        }
      }
      return {
        ...state,
        brand_language: brandLanguage,
      }
    }
    default:
      return state
  }
}

function getBrandFormHelperFunctions() {
  const formReducerMethods = formHelperMethodFactory<'BrandFormReducerState'>()

  // Brand language form helper functions
  const setBrandLanguage = (action: FormReducerAction, idx: number): BrandFormReducerAction => ({
    type: UPDATE_BRAND_LANGUAGE,
    payload: { action, idx },
  })

  const addNewFormBlock = (blockType: BlockTypes): BrandFormReducerAction => ({
    type: blockType === 'content_blocks' ? TRANSFORM_CONTENT_BLOCKS : TRANSFORM_BRAND_LANGUAGE,
    payload: { actionType: 'add', idx: null },
  })

  const removeFormBlock = (blockType: BlockTypes, idx: number): BrandFormReducerAction => ({
    type: blockType === 'content_blocks' ? TRANSFORM_CONTENT_BLOCKS : TRANSFORM_BRAND_LANGUAGE,
    payload: { actionType: 'remove', idx },
  })

  return {
    ...formReducerMethods,
    setBrandLanguage,
    addNewFormBlock,
    removeFormBlock,
  }
}

export const formHelperFunctions = getBrandFormHelperFunctions()

export default brandFormReducer
